import { inject, Injectable, NgZone } from '@angular/core';

/**
 * Publishes the visible viewport's position as CSS custom properties, so fixed overlays can follow
 * it instead of the layout viewport.
 *
 * @remarks
 * `position: fixed` is resolved against the **layout** viewport, which the soft keyboard does not
 * change. What the keyboard changes is the **visual** viewport: it shrinks to the area still on
 * screen, and the browser scrolls it down inside the layout viewport to keep the focused field
 * visible. A toast pinned to `top: 12px` is therefore pinned to a line that is now above the top of
 * the screen — the user never sees it. That is the reported bug on the POST-SAF-AOC public-key
 * field, and it applies to every toast raised while a field has focus.
 *
 * `window.visualViewport.offsetTop` is exactly the gap between the two, so writing it to a custom
 * property lets CSS re-pin anything that needs to track what is actually visible. `--visual-
 * viewport-height` is published alongside it for anything that needs to size to the visible area.
 *
 * Listeners run outside Angular: the keyboard animating open fires `resize` and `scroll` many times
 * per second, and this only writes two CSS variables — there is nothing for change detection to do.
 *
 * Browsers without `visualViewport` (and SSR) get nothing, and the CSS fallbacks — `0px` and
 * `100vh` — leave the previous behaviour untouched.
 */
@Injectable({ providedIn: 'root' })
export class VisualViewportService {

  private readonly zone = inject(NgZone);
  private started = false;

  /** Offset of the visible area from the top of the layout viewport. */
  private static readonly TOP_VAR = '--visual-viewport-top';
  /** Height of the visible area. */
  private static readonly HEIGHT_VAR = '--visual-viewport-height';

  /**
   * Begins tracking. Safe to call more than once; only the first call binds.
   *
   * Nothing unbinds: this is a root-provided singleton started from `AppComponent`, so it lives
   * as long as the document the listeners are attached to.
   */
  start(): void {
    if (this.started || typeof window === 'undefined') {
      return;
    }
    const viewport = window.visualViewport;
    if (!viewport) {
      return;
    }
    this.started = true;

    const publish = () => {
      const root = document.documentElement;
      root.style.setProperty(VisualViewportService.TOP_VAR, `${Math.max(0, viewport.offsetTop)}px`);
      root.style.setProperty(VisualViewportService.HEIGHT_VAR, `${viewport.height}px`);
    };

    this.zone.runOutsideAngular(() => {
      publish();
      viewport.addEventListener('resize', publish);
      // `scroll` too, not just `resize`: pinch-zooming and the browser's own scroll-to-focused-field
      // move the visual viewport without changing its size, and only this event reports that.
      viewport.addEventListener('scroll', publish);
    });
  }
}
